/**
 * Lobby screen CSS styles
 * Extracted from lobbyScreen.ts to reduce file size
 */

export const LOBBY_STYLES = `
  .lobby-container {
    width: 100%;
    padding: 20px 15px;
    padding-top: max(20px, env(safe-area-inset-top, 20px));
    padding-bottom: max(20px, env(safe-area-inset-bottom, 20px));
    padding-left: max(15px, env(safe-area-inset-left, 15px));
    padding-right: max(15px, env(safe-area-inset-right, 15px));
    font-family: var(--vv-font-ui);
    color: var(--vv-text);
    min-height: 100vh;
    box-sizing: border-box;
    overflow-y: auto;
  }

  /* ---- Lobby room header ------------------------------------------- */
  .lobby-room-header {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
    margin-bottom: 16px;
    flex-wrap: wrap;
  }
  .lobby-room-title.vv-title {
    font-size: clamp(28px, 7vw, 52px);
    margin: 0;
    text-align: center;
  }
  .lobby-room-badge {
    font-family: var(--vv-font-ui);
    font-weight: 700;
    font-size: 12px;
    letter-spacing: 1px;
    color: var(--vv-ink);
    background: linear-gradient(180deg, #ffd860, #e89a1c);
    padding: 5px 12px;
    border-radius: 999px;
    box-shadow: inset 0 1px 0 rgba(255,255,255,0.5), 0 2px 4px rgba(0,0,0,0.4);
  }
  
  .lobby-title {
    text-align: center;
    font-size: clamp(24px, 6vw, 48px);
    margin-bottom: 5px;
    text-shadow: 2px 2px 4px rgba(0,0,0,0.5);
  }
  
  .lobby-version {
    text-align: center;
    font-size: 11px;
    color: #8aa;
    margin-bottom: 15px;
    font-family: monospace;
    opacity: 0.7;
  }

  /* ---- Title / main menu (Fusion identity) -------------------------- */
  .lobby-main {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 14px;
    min-height: 100dvh;
    position: relative;
    z-index: 1;
  }
  .lobby-main > * { position: relative; z-index: 1; }
  .title-wrap { text-align: center; margin-bottom: 6px; }
  .title-mascot {
    width: clamp(64px, 17vw, 104px);
    height: clamp(64px, 17vw, 104px);
    margin: 0 auto -10px;
    animation: mascot-bob 3.2s ease-in-out infinite;
  }
  @keyframes mascot-bob {
    0%, 100% { transform: translateY(0) rotate(-3deg); }
    50% { transform: translateY(-6px) rotate(3deg); }
  }
  @media (prefers-reduced-motion: reduce) {
    .title-mascot { animation: none; }
  }
  .lobby-title.vv-title {
    font-size: clamp(56px, 16vw, 132px);
    margin: 0;
    text-align: center;
  }
  .title-tagline {
    font-family: var(--vv-font-ui);
    font-weight: 600;
    color: var(--vv-text-dim);
    font-size: clamp(13px, 3.4vw, 16px);
    letter-spacing: 1px;
    margin-top: 8px;
  }
  .lobby-main .lobby-menu {
    width: min(340px, 86vw);
    margin: 0;
    gap: 14px;
  }
  .main-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    width: 100%;
  }
  .btn-ico { font-size: 1.05em; line-height: 1; filter: drop-shadow(0 1px 1px rgba(0,0,0,0.4)); }
  .lobby-main .main-name {
    width: min(340px, 86vw);
    margin: 4px 0 0;
  }
  .main-name label {
    display: block;
    font-family: var(--vv-font-ui);
    font-weight: 600;
    color: var(--vv-text-dim);
    font-size: 13px;
    margin-bottom: 8px;
    text-align: center;
  }
  .lobby-main .main-name input {
    width: 100%;
    text-align: center;
    font-size: 16px;
    background: rgba(8,16,34,0.6);
    border: 2px solid var(--vv-stroke);
    border-radius: 10px;
    color: var(--vv-text);
    font-family: var(--vv-font-ui);
    font-weight: 600;
  }
  .lobby-main .main-name input:focus {
    border-color: var(--vv-gold);
    box-shadow: 0 0 0 3px rgba(255,198,59,0.25);
    outline: none;
  }
  .lobby-main .loading-message { color: var(--vv-text-dim); }
  
  .lobby-menu {
    display: flex;
    flex-direction: column;
    gap: 12px;
    max-width: 320px;
    margin: 0 auto 25px;
    padding: 0 10px;
  }
  
  .lobby-btn {
    font-family: var(--vv-font-ui);
    font-weight: 700;
    padding: 14px 22px;
    min-height: 48px;
    font-size: clamp(15px, 4vw, 17px);
    color: #fff;
    border: none;
    border-radius: 12px;
    cursor: pointer;
    letter-spacing: 0.3px;
    text-shadow: 0 2px 0 rgba(0,0,0,0.3);
    background: linear-gradient(180deg, #5b9be8, #2f63b8);
    box-shadow:
      inset 0 2px 0 rgba(255,255,255,0.4),
      inset 0 -3px 0 rgba(0,0,0,0.25),
      0 4px 0 #1d447f,
      0 6px 12px rgba(0,0,0,0.35);
    transition: transform 0.1s ease, filter 0.1s ease, box-shadow 0.1s ease;
    touch-action: manipulation;
    -webkit-tap-highlight-color: transparent;
  }
  
  .lobby-btn.small {
    padding: 9px 14px;
    min-height: 0;
    font-size: 13px;
    box-shadow:
      inset 0 1px 0 rgba(255,255,255,0.35),
      0 3px 0 #1d447f,
      0 4px 8px rgba(0,0,0,0.3);
  }
  
  .lobby-btn:not(:disabled):hover { filter: brightness(1.08); }
  
  .lobby-btn:not(:disabled):active {
    transform: translateY(3px);
    box-shadow:
      inset 0 2px 0 rgba(255,255,255,0.3),
      0 1px 0 #1d447f,
      0 2px 6px rgba(0,0,0,0.35);
  }
  .lobby-btn:focus-visible { outline: 3px solid var(--vv-cyan); outline-offset: 3px; }
  
  .lobby-btn:disabled {
    opacity: 0.45;
    cursor: not-allowed;
    filter: grayscale(0.4);
  }
  
  .lobby-btn.primary {
    background: linear-gradient(180deg, #5fd463, #2f9b35);
    box-shadow:
      inset 0 2px 0 rgba(255,255,255,0.4),
      inset 0 -3px 0 rgba(0,0,0,0.25),
      0 4px 0 #1d6b22,
      0 6px 12px rgba(0,0,0,0.35);
    color: white;
  }
  
  .lobby-btn.secondary {
    background: linear-gradient(180deg, #33446b, #212f4f);
    box-shadow:
      inset 0 2px 0 rgba(255,255,255,0.25),
      inset 0 -3px 0 rgba(0,0,0,0.25),
      0 4px 0 #141d33,
      0 6px 12px rgba(0,0,0,0.35);
    color: white;
  }
  
  .lobby-input-group, .join-section {
    max-width: 320px;
    margin: 0 auto 20px;
    text-align: center;
    padding: 0 10px;
  }
  
  .lobby-input-group label, .join-section label {
    display: block;
    margin-bottom: 8px;
    font-size: 14px;
  }
  
  .lobby-input-group input, .join-section input {
    width: 100%;
    padding: 14px 12px;
    font-size: 16px;
    font-family: var(--vv-font-ui);
    font-weight: 600;
    border: 2px solid var(--vv-stroke);
    border-radius: 10px;
    background: rgba(8,16,34,0.6);
    color: #fff;
    text-align: center;
    margin-bottom: 12px;
    box-sizing: border-box;
    -webkit-appearance: none;
  }
  .lobby-input-group input:focus, .join-section input:focus {
    outline: none;
    border-color: var(--vv-gold);
    box-shadow: 0 0 0 3px rgba(255,198,59,0.25);
  }
  
  .join-section input {
    font-family: monospace;
    font-size: 18px;
    letter-spacing: 2px;
    text-transform: uppercase;
  }
  
  .room-code-input-wrapper {
    display: flex;
    align-items: center;
    background: rgba(8,16,34,0.6);
    border: 2px solid var(--vv-stroke);
    border-radius: 10px;
    margin-bottom: 12px;
    overflow: hidden;
  }
  .room-code-input-wrapper:focus-within {
    border-color: var(--vv-gold);
    box-shadow: 0 0 0 3px rgba(255,198,59,0.25);
  }
  
  .room-code-prefix {
    padding: 14px 0 14px 12px;
    font-family: var(--vv-font-display);
    font-size: 18px;
    letter-spacing: 2px;
    color: var(--vv-gold);
    background: rgba(8,16,34,0.5);
    user-select: none;
  }
  
  .room-code-input-wrapper input {
    flex: 1;
    border: none !important;
    margin-bottom: 0 !important;
    border-radius: 0 !important;
    padding-left: 4px !important;
    text-align: left !important;
  }
  
  .room-code-input-wrapper input:focus {
    outline: none;
  }
  
  .room-code-section {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    margin-bottom: 15px;
    padding: 16px 15px;
    border-radius: 14px;
    position: sticky;
    top: 0;
    z-index: 100;
  }
  
  .room-share-info {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
  }
  
  .room-code-label {
    font-size: 12px;
    color: #888;
  }
  
  .room-code {
    font-family: var(--vv-font-display);
    font-size: clamp(22px, 6vw, 30px);
    color: var(--vv-gold);
    letter-spacing: 3px;
    word-break: break-all;
    text-shadow: 0 2px 0 rgba(0,0,0,0.4);
  }
  
  .room-qr-code {
    background: white;
    padding: 8px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  
  .room-qr-code svg {
    display: block;
  }
  
  .loading-message {
    text-align: center;
    font-size: 18px;
    color: #888;
    padding: 40px 20px;
  }
  
  .lobby-sections {
    display: flex;
    flex-direction: column;
    gap: 20px;
    margin-bottom: 20px;
  }
  
  .lobby-section h2 {
    font-family: var(--vv-font-display);
    font-weight: 400;
    font-size: 19px;
    letter-spacing: 0.5px;
    color: var(--vv-text);
    margin-bottom: 12px;
    padding-bottom: 8px;
    border-bottom: 2px solid var(--vv-gold);
  }
  
  .players-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  
  .player-item {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 12px;
    background: linear-gradient(180deg, #1b2f54, #142544);
    border-radius: 10px;
    box-shadow: inset 0 1px 0 rgba(255,255,255,0.08), inset 0 0 0 1px rgba(82,136,214,0.25);
    flex-wrap: wrap;
  }
  
  .player-item.local {
    box-shadow: inset 0 1px 0 rgba(255,255,255,0.12), inset 0 0 0 2px var(--vv-go);
  }
  
  .player-name {
    font-size: 14px;
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  
  .host-badge {
    background: #ff9800;
    color: #000;
    padding: 2px 6px;
    border-radius: 3px;
    font-size: 10px;
    font-weight: bold;
    flex-shrink: 0;
  }
  
   .player-teams {
     color: #888;
     font-size: 11px;
     flex-shrink: 0;
   }
   
   .player-status {
     font-size: 16px;
     margin-right: 8px;
   }
   
   .player-item.ready {
     border-left: 3px solid #4CAF50;
   }
   
   .player-item.not-ready {
     border-left: 3px solid #ff9800;
   }
   
   .player-item.spectator {
     border-left: 3px solid #9c27b0;
     opacity: 0.8;
   }
   
   .spectator-badge {
     background: #9c27b0;
     color: white;
     padding: 2px 6px;
     border-radius: 3px;
     font-size: 10px;
     font-weight: bold;
     flex-shrink: 0;
   }
   
   .spectator-toggle-small {
     display: flex;
     align-items: center;
     gap: 4px;
     font-size: 11px;
     color: #aaa;
     cursor: pointer;
     margin-left: 8px;
   }
   
   .spectator-toggle-small input {
     width: 14px;
     height: 14px;
     accent-color: #9c27b0;
     cursor: pointer;
   }
   
   .spectator-toggle-small span {
     user-select: none;
   }
   
   .kick-btn {
     padding: 4px 10px;
     background: #f44336;
     color: white;
     border: none;
     border-radius: 4px;
     font-size: 11px;
     cursor: pointer;
     margin-left: auto;
     flex-shrink: 0;
     transition: background 0.2s;
   }
   
   .kick-btn:hover {
     background: #d32f2f;
   }
   
   .kick-btn:active {
     transform: scale(0.95);
   }
   
   .teams-list {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }
  
  .team-slot {
    /* Deterministic 2-row card: swatch on the left (spanning), the team name
       on top, the controls (alliance / owner / claim) on the row below. This
       never overflows its column no matter how narrow the alliance column
       gets (portrait, landscape-phone, 4 alliances, etc.). */
    display: grid;
    grid-template-columns: auto minmax(0, 1fr);
    align-items: center;
    column-gap: 10px;
    row-gap: 8px;
    padding: 12px;
    background: linear-gradient(180deg, #1b2f54, #122140);
    border-radius: 12px;
    box-shadow: inset 0 1px 0 rgba(255,255,255,0.08), inset 0 0 0 2px rgba(82,136,214,0.22), 0 3px 8px rgba(0,0,0,0.3);
  }
  
  .team-slot.claimed {
    box-shadow: inset 0 1px 0 rgba(255,255,255,0.12), inset 0 0 0 2px var(--vv-gold), 0 3px 10px rgba(0,0,0,0.35);
  }
  
  .team-color {
    width: 34px;
    height: 34px;
    border-radius: 50%;
    border: 2px solid rgba(255,255,255,0.85);
    box-shadow: inset 0 3px 5px rgba(255,255,255,0.55), inset 0 -4px 6px rgba(0,0,0,0.45), 0 2px 4px rgba(0,0,0,0.4);
    flex-shrink: 0;
    grid-column: 1;
    grid-row: 1 / span 2;
    align-self: center;
  }
  
  .team-info {
    grid-column: 2;
    grid-row: 1;
    min-width: 0;
  }

  /* The controls row (alliance button + owner + claim/release). Lives on the
     card's second grid row, so it always has the full card width and never
     spills past the column edge. */
  .team-slot-actions {
    grid-column: 2;
    grid-row: 2;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
    min-width: 0;
  }
  .team-slot-actions .alliance-btn,
  .team-slot-actions .team-btn {
    flex-shrink: 0;
  }
  
  .team-name {
    font-weight: bold;
    font-size: 14px;
  }
  
  .team-name-input {
    font-weight: bold;
    font-size: 14px;
    background: transparent;
    border: 1px solid transparent;
    color: #fff;
    padding: 4px 6px;
    border-radius: 4px;
    width: 100%;
    transition: all 0.2s;
  }
  
  .team-name-input:not([readonly]):hover {
    background: #444;
    border-color: #555;
  }
  
  .team-name-input:not([readonly]):focus {
    background: #444;
    border-color: #4CAF50;
    outline: none;
  }
  
  .team-name-input[readonly] {
    cursor: default;
  }
  
  .team-worms {
    font-size: 11px;
    color: #888;
    margin-top: 2px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  
  .team-owner {
    color: #888;
    font-size: 12px;
    flex: 1 1 auto;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  
  .team-btn {
    padding: 10px 18px;
    min-height: 44px;
    border: none;
    border-radius: 10px;
    cursor: pointer;
    font-family: var(--vv-font-ui);
    font-size: 13px;
    font-weight: 700;
    color: #fff;
    text-shadow: 0 1px 0 rgba(0,0,0,0.3);
    touch-action: manipulation;
    -webkit-tap-highlight-color: transparent;
    flex-shrink: 0;
    transition: transform 0.1s ease, filter 0.1s ease;
  }
  .team-btn:not(:disabled):hover { filter: brightness(1.08); }
  .team-btn:focus-visible { outline: 3px solid var(--vv-cyan); outline-offset: 2px; }
  
  .alliance-btn {
    width: 34px;
    height: 34px;
    border: 2px solid #9c27b0;
    border-radius: 6px;
    background: #2a2a2a;
    color: #ce93d8;
    font-size: 14px;
    font-weight: bold;
    cursor: pointer;
    flex-shrink: 0;
    touch-action: manipulation;
    -webkit-tap-highlight-color: transparent;
  }
  
  .alliance-btn:hover:not(:disabled) {
    background: #9c27b0;
    color: #fff;
  }
  
  .alliance-btn:disabled {
    border-color: #555;
    color: #666;
    cursor: default;
  }
  
  .weapon-whitelist {
    margin: 8px 0;
  }
  
  .weapon-whitelist summary {
    cursor: pointer;
    color: #ccc;
    font-size: 13px;
    padding: 4px 0;
  }
  
  .whitelist-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 2px 10px;
    padding: 6px 0 4px 12px;
  }
  
  .team-btn.claim {
    background: linear-gradient(180deg, #5b9be8, #2f63b8);
    box-shadow: inset 0 1px 0 rgba(255,255,255,0.4), 0 3px 0 #1d447f;
  }
  
  .team-btn.release {
    background: linear-gradient(180deg, #f4675c, #c4382c);
    box-shadow: inset 0 1px 0 rgba(255,255,255,0.35), 0 3px 0 #8c241b;
  }
  
  .team-btn:disabled {
    background: #2a3654;
    color: #6c7a98;
    box-shadow: none;
    cursor: not-allowed;
  }
  
  .team-btn:active:not(:disabled) {
    transform: scale(0.95);
  }
  
  .lobby-actions {
    display: flex;
    justify-content: center;
    gap: 15px;
    margin-bottom: 15px;
    flex-wrap: wrap;
    padding: 0 10px;
  }
  
  .lobby-actions .lobby-btn {
    flex: 1;
    max-width: 160px;
    min-width: 120px;
  }
  
  .lobby-hint {
    text-align: center;
    color: #888;
    font-size: 13px;
    padding: 0 15px 20px;
  }
  
  @media (min-width: 600px) {
    .lobby-container {
      padding: 30px 20px;
    }
    
    .lobby-sections {
      display: grid;
      grid-template-columns: 1fr 2fr;
      gap: 25px;
    }
    
    .room-code-section {
      flex-direction: row;
      justify-content: center;
      align-items: center;
      gap: 20px;
    }
    
    .room-share-info {
      align-items: flex-start;
    }
    
    .team-slot {
      flex-wrap: nowrap;
    }
    
    .team-owner {
      min-width: 80px;
    }
  }
  
  @media (min-width: 768px) {
    .lobby-container {
      padding: 40px 30px;
    }
    
    .lobby-header h1 {
      font-size: 32px;
    }
    
    .lobby-sections {
      grid-template-columns: 1fr 2.5fr;
      gap: 30px;
    }
    
    .room-code-display {
      font-size: 32px;
      letter-spacing: 6px;
      padding: 18px 30px;
    }
    
    .teams-section h2,
    .players-section h2 {
      font-size: 18px;
    }
    
    .team-slot {
      padding: 14px 16px;
    }
    
    .team-color {
      width: 36px;
      height: 36px;
    }
    
    .team-name, .team-name-input {
      font-size: 15px;
    }
    
    .team-worms {
      font-size: 12px;
    }
    
    .team-btn {
      padding: 12px 20px;
      font-size: 14px;
    }
    
    .lobby-btn {
      padding: 16px 28px;
      font-size: 16px;
    }
    
    .lobby-actions .lobby-btn {
      max-width: 200px;
      min-width: 150px;
    }
    
    .setting-toggle, .setting-select {
      padding: 12px 16px;
    }
    
    .setting-toggle span {
      font-size: 15px;
    }
  }
  
  @media (max-width: 400px) {
    .team-slot {
      padding: 10px;
    }
    
    .team-info {
      min-width: 100px;
    }
    
    .team-worms {
      display: none;
    }
    
    .team-owner {
      display: none;
    }
  }
  
  .settings-section {
    margin-top: 10px;
  }
  
  .settings-group {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  
  .setting-toggle {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 11px 12px;
    background: rgba(27,47,84,0.5);
    border-radius: 9px;
    box-shadow: inset 0 0 0 1px rgba(82,136,214,0.18);
    cursor: pointer;
    transition: background 0.15s ease;
  }
  
  .setting-toggle:hover {
    background: rgba(35,60,108,0.6);
  }
  
  .setting-toggle input[type="checkbox"] {
    width: 18px;
    height: 18px;
    accent-color: #4CAF50;
    cursor: pointer;
  }
  
  .setting-toggle span {
    font-size: 14px;
    user-select: none;
  }
  
  .setting-select {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    padding: 10px 12px;
    background: rgba(27,47,84,0.5);
    border-radius: 9px;
    box-shadow: inset 0 0 0 1px rgba(82,136,214,0.18);
    margin-bottom: 4px;
  }
  
  .setting-select label {
    font-size: 14px;
    white-space: nowrap;
  }
  
  .setting-select select {
    flex: 1;
    max-width: 150px;
    padding: 8px 10px;
    border: 2px solid var(--vv-stroke);
    border-radius: 8px;
    background: rgba(8,16,34,0.7);
    color: white;
    font-family: var(--vv-font-ui);
    font-weight: 600;
    font-size: 14px;
    cursor: pointer;
  }
  
  .setting-select select:hover {
    border-color: var(--vv-gold);
  }
  
  .setting-seed {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 12px;
    background: rgba(27,47,84,0.5);
    border-radius: 9px;
    box-shadow: inset 0 0 0 1px rgba(82,136,214,0.18);
    margin-bottom: 4px;
  }
  
  .setting-seed label {
    font-size: 14px;
    white-space: nowrap;
  }
  
  .setting-seed input {
    flex: 1;
    padding: 8px 10px;
    border: 2px solid var(--vv-stroke);
    border-radius: 8px;
    background: rgba(8,16,34,0.7);
    color: white;
    font-family: monospace;
    font-size: 14px;
  }
  .setting-seed input:focus { outline: none; border-color: var(--vv-gold); }
  
  .seed-btn {
    padding: 8px 12px;
    min-height: 40px;
    background: linear-gradient(180deg, #33446b, #212f4f);
    border: none;
    border-radius: 8px;
    color: white;
    cursor: pointer;
    font-size: 16px;
    box-shadow: inset 0 1px 0 rgba(255,255,255,0.25), 0 2px 0 #141d33;
    transition: filter 0.15s ease;
  }
  
  .seed-btn:hover {
    filter: brightness(1.12);
  }
  
  .setting-number {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 12px;
    background: rgba(27,47,84,0.5);
    border-radius: 9px;
    box-shadow: inset 0 0 0 1px rgba(82,136,214,0.18);
    margin-top: 4px;
  }
  
  .setting-number label {
    font-size: 14px;
    white-space: nowrap;
  }
  
  .setting-number input {
    width: 70px;
    padding: 8px 10px;
    border: 2px solid var(--vv-stroke);
    border-radius: 8px;
    background: rgba(8,16,34,0.7);
    color: white;
    font-family: var(--vv-font-ui);
    font-weight: 600;
    font-size: 14px;
    text-align: center;
  }
  .setting-number input:focus { outline: none; border-color: var(--vv-gold); }
  
  .connection-badge {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 6px 12px;
    border-radius: 16px;
    font-size: 12px;
    font-weight: bold;
    margin: 0 auto 15px;
    width: fit-content;
  }
  
  .conn-ipv6 {
    background: linear-gradient(135deg, #4CAF50, #45a049);
    color: white;
  }
  
  .conn-ipv4 {
    background: linear-gradient(135deg, #2196F3, #1976D2);
    color: white;
  }
  
  .conn-relay {
    background: linear-gradient(135deg, #ff9800, #f57c00);
    color: white;
  }
  
  .reconnect-modal {
    background: #1a1a1a;
    border: 2px solid #444;
    border-radius: 12px;
    padding: 30px;
    text-align: center;
    max-width: 300px;
  }
  
  .reconnect-spinner {
    width: 40px;
    height: 40px;
    border: 4px solid #333;
    border-top: 4px solid #4CAF50;
    border-radius: 50%;
    margin: 0 auto 20px;
    animation: spin 1s linear infinite;
  }
  
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
  
  .reconnect-status {
    font-size: 18px;
    font-weight: bold;
    margin-bottom: 10px;
  }
  
  .reconnect-hint {
    font-size: 14px;
    color: #888;
    margin-bottom: 20px;
  }
  
  .connection-status {
    display: inline-block;
    padding: 4px 10px;
    border-radius: 12px;
    font-size: 12px;
    font-weight: bold;
    margin-left: 10px;
  }
  
  .status-connected {
    background: #4CAF50;
    color: white;
  }
  
  .status-reconnecting {
    background: #ff9800;
    color: white;
    animation: pulse 1.5s ease-in-out infinite;
  }
  
  .status-disconnected {
    background: #f44336;
    color: white;
  }
  
   @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.6; }
  }

  .title-settings-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(6, 16, 31, 0.82);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 2000;
  }

  .title-settings-panel {
    background: linear-gradient(180deg, var(--vv-panel-top), var(--vv-panel-bottom));
    border-radius: 16px;
    box-shadow:
      inset 0 2px 0 rgba(255,255,255,0.22),
      inset 0 0 0 2px rgba(255,198,59,0.55),
      inset 0 -4px 10px rgba(0,0,0,0.35),
      0 8px 24px rgba(0,0,0,0.5);
    padding: 24px;
    width: 90%;
    max-width: 400px;
    max-height: 80vh;
    overflow-y: auto;
    color: var(--vv-text);
    font-family: var(--vv-font-ui);
  }

  .title-settings-panel h2 {
    font-family: var(--vv-font-display);
    font-weight: 400;
    color: var(--vv-gold);
    text-align: center;
    margin-bottom: 20px;
    font-size: 30px;
    letter-spacing: 1px;
    text-shadow: 0 2px 0 rgba(0,0,0,0.4);
  }

  .title-settings-panel .settings-group {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .title-settings-panel .setting-slider {
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 11px 12px;
    background: rgba(27,47,84,0.5);
    border-radius: 9px;
    box-shadow: inset 0 0 0 1px rgba(82,136,214,0.18);
  }

  .title-settings-panel .setting-slider-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 14px;
  }

  .title-settings-panel .setting-slider input[type="range"] {
    width: 100%;
    accent-color: var(--vv-gold);
    cursor: pointer;
  }

  .title-settings-panel .setting-divider {
    text-align: center;
    color: var(--vv-text-dim);
    font-weight: 600;
    font-size: 12px;
    padding: 8px 0 4px;
  }

  .title-settings-panel .log-categories {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 6px;
  }

  .title-settings-panel .log-categories .setting-toggle {
    padding: 8px 10px;
    font-size: 13px;
  }

  .title-settings-panel .settings-actions {
    margin-top: 16px;
    display: flex;
    justify-content: center;
  }

  /* ---- Alliance columns (GunBound-style sides) ---- */
  .alliance-toolbar {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 10px;
    flex-wrap: wrap;
  }
  .alliance-count-label {
    font-size: 13px;
    opacity: 0.85;
    display: flex;
    align-items: center;
    gap: 6px;
  }
  .alliance-count-select {
    padding: 4px 8px;
    border-radius: 6px;
  }
  .randomize-on-start-label {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    opacity: 0.9;
  }
  .randomize-on-start-check {
    cursor: pointer;
  }
  .alliance-columns {
    display: grid;
    /* auto-fit + min() keeps columns from ever overflowing: on narrow
       (portrait) screens the alliances stack into one column, on wide screens
       they sit side by side (GunBound sides). No breakpoint required, so it
       works regardless of device width / orientation. */
    grid-template-columns: repeat(auto-fit, minmax(min(100%, 240px), 1fr));
    gap: 12px;
    width: 100%;
    min-width: 0;
  }
  .alliance-column {
    border: 1px solid rgba(255, 255, 255, 0.12);
    border-top: 3px solid var(--alliance-accent, #888);
    border-radius: 10px;
    padding: 8px;
    background: rgba(0, 0, 0, 0.18);
    display: flex;
    flex-direction: column;
    gap: 8px;
    min-width: 0;
  }
  .alliance-header {
    font-weight: 700;
    font-size: 14px;
    color: var(--alliance-accent, #ccc);
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 2px 4px 6px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  }
  .alliance-tag {
    font-size: 11px;
    font-weight: 600;
    opacity: 0.7;
    color: #fff;
  }
  .alliance-empty {
    font-size: 12px;
    opacity: 0.5;
    text-align: center;
    padding: 10px 0;
  }
`;
